"use client";

import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import Image from "next/image";

export function MainScreen() {
  return (
    <section className="relative min-h-screen bg-background overflow-hidden pt-20 md:pt-24 ">
      <div className="absolute inset-0">
        <Image
          src="/images/heros/main.jpg"
          alt="Taormina concierge background"
          fill
          className="object-cover"
          sizes="100vw"
          priority={true}
          
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70" />
      </div>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 min-h-screen">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 min-h-screen max-w-7xl mx-auto">
          {/* Left side - Content */}
          <div className="flex flex-col justify-center py-12 lg:py-32 space-y-8 lg:space-y-12 max-w-2xl mx-auto lg:mx-0 z-10 text-white">
            {/* Small badges */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="flex flex-wrap gap-2 sm:gap-3"
            >
              {[
                "Authentic",
                "Personal",
                "Exclusive",
                "Friendly",
                "Charming",
              ].map((badge, i) => (
                <span
                  key={badge}
                  className="text-xs uppercase tracking-wider text-white/80 font-sans border border-white/30 px-2.5 py-1 rounded-full bg-white/10"
                >
                  {badge}
                </span>
              ))}
            </motion.div>

            {/* Main heading */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="space-y-4 lg:space-y-6"
            >
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-white leading-[1.1]">
                Premium concierge
                <br />
                services in Taormina
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-white/90 leading-relaxed font-sans max-w-xl">
                Exclusive excursions, luxury hotel bookings, private boat tours,
                seamless transfers, restaurant reservations, and unforgettable
                wedding planning — all tailored to your desires.
              </p>
            </motion.div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4"
            >
              <Button
                asChild
                size="lg"
                className="group bg-primary text-primary-foreground hover:bg-primary/90 w-full sm:w-auto"
              >
                <Link
                  href="#contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book Your Experience
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="w-full sm:w-auto bg-transparent"
              >
                <Link href="#services">Explore Services</Link>
              </Button>
            </motion.div>
          </div>

          {/* Right side - Centered Image Composition */}
          {/* Right side - Centered Image Composition */}
          <div className="relative hidden lg:flex items-center justify-center">
            <div className="relative w-full max-w-[500px] h-[500px]">
              {/* Main large image - center */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 rounded-3xl overflow-hidden shadow-2xl z-10"
              >
                <Image
                  src="/images/mosaic/1.webp"
                  alt="Taormina view"
                  fill
                  className="object-cover"
                  sizes="(max-width: 1024px) 0px, 320px"
                  priority
                />
              </motion.div>

              {/* Top right accent image */}
              <motion.div
                initial={{ opacity: 0, y: 30, rotate: -8 }}
                animate={{ opacity: 1, y: 0, rotate: -6 }}
                transition={{ delay: 0.6, duration: 0.8 }}
                className="absolute top-0 right-0 w-48 h-48 rounded-2xl overflow-hidden shadow-xl z-20"
              >
                <Image
                  src="/images/mosaic/2.webp"
                  alt="Boat tour"
                  fill
                  sizes="(max-width: 1024px) 0px, 192px"
                  className="object-cover"
                />
              </motion.div>

              {/* Top left accent image */}
              <motion.div
                initial={{ opacity: 0, x: 30, rotate: 8 }}
                animate={{ opacity: 1, x: 0, rotate: 5 }}
                transition={{ delay: 0.7, duration: 0.8 }}
                className="absolute top-4 left-0 w-40 h-40 rounded-2xl overflow-hidden shadow-xl z-20"
              >
                <Image
                  src="/images/mosaic/3.webp"
                  alt="Luxury transfer"
                  fill
                  sizes="(max-width: 1024px) 0px, 160px"
                  className="object-cover"
                />
              </motion.div>

              {/* Bottom left accent image */}
              <motion.div
                initial={{ opacity: 0, y: -30, rotate: 8 }}
                animate={{ opacity: 1, y: 0, rotate: 6 }}
                transition={{ delay: 0.8, duration: 0.8 }}
                className="absolute bottom-0 left-0 w-44 h-44 rounded-2xl overflow-hidden shadow-xl z-20"
              >
                <Image
                  src="/images/mosaic/4.webp"
                  alt="Restaurant"
                  fill
                  sizes="(max-width: 1024px) 0px, 176px"
                  className="object-cover"
                />
              </motion.div>

              {/* Bottom right accent image */}
              <motion.div
                initial={{ opacity: 0, y: 30, rotate: -8 }}
                animate={{ opacity: 1, y: 0, rotate: -5 }}
                transition={{ delay: 1, duration: 0.8 }}
                className="absolute bottom-8 right-12 w-40 h-40 rounded-2xl overflow-hidden shadow-xl z-20"
              >
                <Image
                  src="/images/mosaic/5.webp"
                  alt="Hotel terrace"
                  fill
                  sizes="(max-width: 1024px) 0px, 160px"
                  className="object-cover"
                />
              </motion.div>

              {/* Middle left accent image */}
              <motion.div
                initial={{ opacity: 0, x: -30, rotate: -10 }}
                animate={{ opacity: 1, x: 0, rotate: -8 }}
                transition={{ delay: 0.9, duration: 0.8 }}
                className="absolute top-1/2 -translate-y-1/2 left-2 w-36 h-36 rounded-2xl overflow-hidden shadow-xl z-20"
              >
                <Image
                  src="/images/mosaic/6.webp"
                  alt="Mount Etna"
                  fill
                  sizes="(max-width: 1024px) 0px, 144px"
                  className="object-cover"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
